import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { LayoutService } from '../../../layout.service';
import * as g from 'src/app/globals';

export interface LineAttachmentRow {
  id: string;
  value: string;

  isImage: boolean;
  allowUploadFile: boolean;
  allowSignature: boolean;
  allowUsingCamera: boolean;
}

@Component({
  selector: 'app-line-attachments',
  templateUrl: './line-attachments.component.html',
  styleUrls: ['./line-attachments.component.less'],
})
export class LineAttachmentsComponent implements OnInit {
  /** passed in from drawer via nzContentParams */
  @Input() attachmentsText: string = '';

  /** same idea as recordProperty.ReadOnly */
  @Input() readOnly = false;
  @Input() required = false;

  /** media settings (optional; defaults mimic typical “file attachment”) */
  @Input() isImage = false;
  @Input() allowUploadFile = true;
  @Input() allowSignature = false;
  @Input() allowUsingCamera = false;

  /** optional: emit changes while editing */
  @Output() attachmentsTextChange = new EventEmitter<string>();

  lines: LineAttachmentRow[] = [];

  isAttachmentDrawerVisible = false;
  baseUrl = g.apiBaseUrl;
  urlSuffix = g.getUrlSuffix();

  activeIndex: number | null = null;

  constructor(
    public layoutService: LayoutService,
    private drawerRef: NzDrawerRef<string>, // close drawer with string result
  ) {}

  ngOnInit(): void {
    this.lines = this.parseAttachmentsText(this.attachmentsText).map((v) =>
      this.newRow(v),
    );
    if (this.lines.length === 0) this.lines = [this.newRow('')];
  }

  get activeLine(): LineAttachmentRow | null {
    if (this.activeIndex === null) return null;
    return this.lines[this.activeIndex] ?? null;
  }

  addLine(): void {
    this.lines = [...this.lines, this.newRow('')];
    this.emitText();
  }

  removeLine(index: number): void {
    this.lines = this.lines.filter((_, i) => i !== index);
    if (this.lines.length === 0) this.lines = [this.newRow('')];
    this.emitText();
  }

  openDrawer(index: number): void {
    this.activeIndex = index;
    this.isAttachmentDrawerVisible = true;
  }

  onFileUploaded(path: string): void {
    if (this.activeIndex === null) return;

    const updated = [...this.lines];
    updated[this.activeIndex] = { ...updated[this.activeIndex], value: path };
    this.lines = updated;

    this.isAttachmentDrawerVisible = false;
    this.activeIndex = null;

    this.emitText();
  }

  preview(index: number): void {
    const value = this.lines[index]?.value || '';
    const safeValue = value.split('#').join('_HASH_');

    window.open(
      g.apiBaseUrl +
        'media/previewfile?path=' +
        encodeURIComponent(safeValue) +
        '&' +
        g.getUrlSuffix(),
    );
  }

  clearAttachment(index: number): void {
    const updated = [...this.lines];
    updated[index] = { ...updated[index], value: '' };
    this.lines = updated;
    this.emitText();
  }

  /** SUBMIT: serialize to string + close drawer with result */
  submit(): void {
    this.lines = this.lines.filter((l) => (l.value || '').trim().length > 0);
    if (this.required && this.lines.length === 0) {
      return;
    }
    this.drawerRef.close(this.serializeLines(this.lines));
  }

  cancel(): void {
    this.drawerRef.close(this.attachmentsText); // or close(null) if you prefer
  }

  trackById(_: number, item: LineAttachmentRow): string {
    return item.id;
  }

  // ---------- helpers ----------

  private newRow(value: string): LineAttachmentRow {
    return {
      id: this.newId(),
      value: value || '',
      isImage: this.isImage,
      allowUploadFile: this.allowUploadFile,
      allowSignature: this.allowSignature,
      allowUsingCamera: this.allowUsingCamera,
    };
  }

  private parseAttachmentsText(text: string): string[] {
    // newline separated; trim empty lines
    return (text || '')
      .split(/\r\n|\n|\r/)
      .map((s) => (s || '').trim())
      .filter((s) => s.length > 0);
  }

  private serializeLines(lines: LineAttachmentRow[]): string {
    return (lines || [])
      .map((l) => (l?.value || '').trim())
      .filter((v) => v.length > 0) // 👈 IGNORE EMPTY VALUES
      .join('\n'); // or ';' if you prefer
  }

  private emitText(): void {
    const text = this.serializeLines(this.lines);
    this.attachmentsTextChange.emit(text);
  }

  private newId(): string {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  isPreviewableImage(path: string): boolean {
    if (!path) return false;

    const lower = path.toLowerCase();

    return (
      lower.endsWith('.png') ||
      lower.endsWith('.jpg') ||
      lower.endsWith('.jpeg') ||
      lower.endsWith('.gif') ||
      lower.endsWith('.webp') ||
      lower.endsWith('.bmp')
    );
  }
}
